import { message as antdMessage } from 'antd';
import React, { useState } from 'react';
import { DangerFilled, InfoFilled, CircleCheckFilled, ErrorFilled, Lightbulb, Spinner, CloseLarge } from '@nackle/origami-icons';
import { Button } from '../../exports';


const getIcon = ( type ) => {
    switch ( type ) {
        case 'info':
            return <InfoFilled className="ant-message-info-icon ant-message-icon"/>;
        case 'success':
            return <CircleCheckFilled className="ant-message-success-icon ant-message-icon"/>;
        case 'warning':
            return <DangerFilled className="ant-message-warning-icon ant-message-icon"/>;
        case 'error': 
            return <ErrorFilled className="ant-message-error-icon ant-message-icon"/>;
        case 'tip':
            return <Lightbulb className="ant-message-tip-icon ant-message-icon"/>;
        case 'loading':
            return <Spinner className="ant-message-loading-icon ant-message-icon"/>;
        default:
            return;
    }
};

/**
 * A popup message
 *
 * {@link https://ant.design/components/message#api}
 *
 * @module message
 * @description Provides a collection of functions to display various types of messages
 * @returns {Object} - a message utility
**/
export const message =  {

    /**
     * @module [message.open] - a function to open a message of any type
     * @param {Object} config - the open message config
     * @param {string} [config.size] - the size of the message
     * @param {string} [config.type] - the message type
     * @param {number} [config.duration] - the amount of seconds the message stays open
     * @param {string} [config.content] - the content of the message
     * @param {boolean} [config.closeable] - whether or not the message has a close button
     * @returns {React.ReactElement} - renders a message component
    **/
    // static methods
    open: function( config ) {
        antdMessage.open( {
            ...config,
            className: config.size === 'large' ? 'ant-message-large' : config.className,
            type: config.type === 'tip' ? 'info' : config.type, // antd doesn't have a tip type, overwrite what we pass to it but use custom icon
            icon: getIcon( config.type ),
            duration: config.duration,
            content: <>
                <div>{ config.content }</div>
                { /* Use closable because it's consistent with other antd parameters,  will add a close icon for messages that doesn't do anything for some reason and is not 
                a part of the documentation, we display: none that icon in the less and use our own. */ }
                { config.closable && <Button size="small" type='link' shape='circle' icon={ <CloseLarge className={ 'ant-message-close-icon' }/> } onClick={ ()=> {
                        // destroy for static methods doesn't work for specific key, this will close all messages
                        // no way i know of to close specific message with static messages. 
                        antdMessage.destroy();
                    } }/> } 
                
            </>,
        } );
    },

    /**
     * @module [message.info] - a function to open an info message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    info: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'info'
        } );
    },

    /**
     * @module [message.success] - a function to open a success message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    success: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'success'
        } );
    },

    /**
     * @module [message.warning] - a function to open a warning message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    warning: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'warning'
        } );
    },

    /**
     * @module [message.error] - a function to open an error message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    error: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'error'
        } );
    },

    /**
     * @module [message.tip] - a function to open a tip message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    tip: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'tip'
        } );
    },

    /**
     * @module [message.loading] - a function to open a loading message
     * @param {string} content - the content of the message
     * @param {number} duration - the amount of seconds the message stays open
     * @param {Function} onClose - the function to call when the message closes
    **/
    loading: function ( content, duration, onClose ) {
        this.open( {
            content,
            duration,
            onClose,
            type: 'loading'
        } );
    },

    // Callback methods
    /**
     * @Function useMessage - a hook to render a message
     * @returns {Object} - a collection of functions to render different types of messages
    **/
    useMessage: () => {
        const [ messageApi, contextHolder ] = antdMessage.useMessage();

        /**
         * @module [message.open] - a function to open a message of any type
         * @param {Object} config - the open message config
         * @param {string} [config.size] - the size of the message
         * @param {string} [config.type] - the message type
         * @param {number} [config.duration] - the amount of seconds the message stays open
         * @param {string} [config.content] - the content of the message
         * @param {boolean} [config.closeable] - whether or not the message has a close button
         * @returns {React.ReactElement} - renders a message component
        **/
        const open = ( config ) => {
            const key = +new Date() + '';// need a unique key for each notification so the destroy() call works, thought about using the message content but no guarantee that it's unique.
            messageApi.open( {
                ...config,
                className: config.size === 'large' ? 'ant-message-large' : config.className,
                type: config.type === 'tip' ? 'info' : config.type, // antd doesn't have a tip type, overwrite what we pass to it but use custom icon
                icon: getIcon( config.type ),
                duration: config.duration,
                key,
                content: <>
                    <div>{ config.content }</div>
                    { /* Use closable because it's consistent with other antd parameters,  will add a close icon for messages that doesn't do anything for some reason and is not 
                    a part of the documentation, we display: none that icon in the less and use our own. */ }
                    { config.closable && <Button size="small" type='link' shape='circle' icon={ <CloseLarge className={ 'ant-message-close-icon' }/> } onClick={ ()=> {
                            messageApi.destroy( key );
                        } }/> } 
                    
                </>,
            } );
        };

        /**
         * @module [message.info] - a function to open an info message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const info = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'info'
            } );
        };

        /**
         * @module [message.success] - a function to open a success message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const success = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'success'
            } );
        };

        /**
         * @module [message.warning] - a function to open a warning message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const warning = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'warning'
            } );
        };

        /**
         * @module [message.error] - a function to open an error message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const error = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'error'
            } );
        };

        /**
         * @module [message.tip] - a function to open a tip message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const tip = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'tip'
            } );
        };

        /**
         * @module [message.loading] - a function to open a loading message
         * @param {string} content - the content of the message
         * @param {number} duration - the amount of seconds the message stays open
         * @param {Function} onClose - the function to call when the message closes
        **/
        const loading = ( content, duration, onClose ) => {
            open( {
                content,
                duration,
                onClose,
                type: 'loading'
            } );
        };
        // we need to insure that the methods keep the same reference between rerenders so we set  as state variable.
        // unnecessary rerenders and side effects can happen otherwise.  
        const [ api ] = useState( { open, info, success, warning, error, tip, loading } );

        return [ api, contextHolder ];
    }
};

